import { createSlice } from '@reduxjs/toolkit';
import {
	getExpenseJournalPreviewEntriesAction,
	getExpenseJournalListAction,
	deleteExpenseJournalAction,
} from 'redux/action/expenseJournalAction';
import { previewOneOffExpenseJournalEntriesAction } from 'redux/action/oneOffAction';

const initialState: any = {
	// Preview entries state
	previewEntriesLoading: false,
	previewEntriesData: [],
	previewEntriesError: null,

	// Journal entries list state
	entriesLoading: false,
	entriesData: [],
	entriesCount: 0,
	entriesError: null,

	// Journal list state (for main journal list page) - Updated for lazy loading
	listLoading: false,
	listData: [],
	listCount: 0,
	listError: null,
	listPagination: {
		currentPage: 1,
		totalPages: 0,
		totalRecords: 0,
		limit: 10,
	},
	isLoadingMore: false,
	hasMore: true,
	firstTimeFetchLoading: true,

	// Create/Update/Delete state
	createLoading: false,
	createError: null,
	updateLoading: false,
	updateError: null,
	deleteLoading: false,
	deleteError: null,

	// Publish/Draft state
	publishLoading: false,
	publishError: null,
	draftLoading: false,
	draftError: null,
};

const expenseJournalSlice = createSlice({
	name: 'expenseJournal',
	initialState,
	reducers: {
		clearExpenseJournalState: () => {
			return initialState;
		},
		clearPreviewEntries: (state) => {
			state.previewEntriesData = [];
			state.previewEntriesError = null;
		},
		clearEntries: (state) => {
			state.entriesData = [];
			state.entriesError = null;
		},
		clearList: (state) => {
			state.listData = [];
			state.listError = null;
		},
		resetJournalList: (state) => {
			state.listData = [];
			state.listPagination.currentPage = 1;
			state.hasMore = true;
		},
	},
	extraReducers: (builder) => {
		// Preview Entries
		builder.addCase(getExpenseJournalPreviewEntriesAction.pending, (state) => {
			state.previewEntriesLoading = true;
			state.previewEntriesData = [];
			state.previewEntriesError = null;
		});
		builder.addCase(
			getExpenseJournalPreviewEntriesAction.fulfilled,
			(state, action) => {
				state.previewEntriesData = action.payload.data || [];
				state.previewEntriesLoading = false;
				state.previewEntriesError = null;
			}
		);
		builder.addCase(
			getExpenseJournalPreviewEntriesAction.rejected,
			(state, action) => {
				state.previewEntriesError = action.payload;
				state.previewEntriesLoading = false;
				state.previewEntriesData = [];
			}
		);

		// Journal List (for main journal list page) - Updated for lazy loading
		builder.addCase(getExpenseJournalListAction.pending, (state, action) => {
			const page = action.meta.arg.page || 1;
			if (page === 1) {
				// First page - initial load
				state.firstTimeFetchLoading = true;
				state.listLoading = true;
			} else {
				// Subsequent pages - infinite scroll
				state.isLoadingMore = true;
			}
			state.listError = null;
		});
		builder.addCase(getExpenseJournalListAction.fulfilled, (state, action) => {
			const page = action.meta.arg.page || 1;
			const newData = action.payload.data?.expenseJournals || [];

			if (page === 1) {
				// First page - replace all data
				state.listData = newData;
				state.firstTimeFetchLoading = false;
				state.listLoading = false;
			} else {
				// Subsequent pages - append to existing data
				state.listData = [...state.listData, ...newData];
				state.isLoadingMore = false;
			}

			// Update pagination info
			state.listPagination = {
				currentPage: page,
				totalPages: action.payload.data?.totalPages || 0,
				totalRecords: action.payload.data?.count || 0,
				limit: action.meta.arg.limit || 10,
			};
			state.listCount = action.payload.data?.count || 0;
			state.hasMore = page < (action.payload.data?.totalPages || 0);
			state.listError = null;
		});
		builder.addCase(getExpenseJournalListAction.rejected, (state, action) => {
			state.listError = action.payload;
			state.firstTimeFetchLoading = false;
			state.listLoading = false;
			state.isLoadingMore = false;
			state.listData = [];
		});

		// Delete Expense Journal
		builder.addCase(deleteExpenseJournalAction.pending, (state) => {
			state.deleteLoading = true;
			state.deleteError = null;
		});
		builder.addCase(deleteExpenseJournalAction.fulfilled, (state, action) => {
			state.deleteLoading = false;
			state.deleteError = null;
			// Remove the deleted journal from the list
			state.listData = state.listData.filter(
				(item: any) => item.id !== action.payload.id
			);
			// Update the count
			state.listCount = Math.max(0, state.listCount - 1);
		});
		builder.addCase(deleteExpenseJournalAction.rejected, (state, action) => {
			state.deleteLoading = false;
			state.deleteError = action.payload;
		});

		// Preview One-Off Expense Journal Entries
		builder.addCase(previewOneOffExpenseJournalEntriesAction.pending, (state) => {
			state.previewEntriesLoading = true;
			state.previewEntriesData = [];
			state.previewEntriesError = null;
		});
		builder.addCase(
			previewOneOffExpenseJournalEntriesAction.fulfilled,
			(state, action) => {
				state.previewEntriesData = action.payload.data || [];
				state.previewEntriesLoading = false;
				state.previewEntriesError = null;
			}
		);
		builder.addCase(
			previewOneOffExpenseJournalEntriesAction.rejected,
			(state, action) => {
				state.previewEntriesError = action.payload;
				state.previewEntriesLoading = false;
				state.previewEntriesData = [];
			}
		);
	},
});

export const {
	clearExpenseJournalState,
	clearPreviewEntries,
	clearEntries,
	clearList,
	resetJournalList,
} = expenseJournalSlice.actions;
export default expenseJournalSlice;
